import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CreditCard, Lock, ArrowLeft, ArrowRight, Check, AlertCircle } from "lucide-react";
import Header from "@/components/shared/Header";
import Footer from "@/components/shared/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFormatPrice } from "@/utils/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuthGate } from "@/hooks/useAuthGate";

// Luhn algorithm check
function luhnCheck(num: string): boolean {
  const digits = num.replace(/\D/g, "");
  if (digits.length < 13 || digits.length > 19) return false;
  let sum = 0;
  let alternate = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = parseInt(digits[i], 10);
    if (alternate) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alternate = !alternate;
  }
  return sum % 10 === 0;
}

function validateExpiry(expiry: string): { valid: boolean; reason?: string } {
  if (!/^\d{2}\/\d{2}$/.test(expiry)) return { valid: false, reason: "format" };
  const [mm, yy] = expiry.split("/").map(Number);
  if (mm < 1 || mm > 12) return { valid: false, reason: "month" };
  const now = new Date();
  const currentYear = now.getFullYear() % 100;
  const currentMonth = now.getMonth() + 1;
  if (yy < currentYear || (yy === currentYear && mm < currentMonth)) return { valid: false, reason: "expired" };
  return { valid: true };
}

type FieldErrors = {
  cardNumber?: string;
  cardName?: string;
  expiry?: string;
  cvv?: string;
};

function InlineError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="flex items-center gap-1 text-xs text-destructive mt-1">
      <AlertCircle className="h-3 w-3 shrink-0" />
      {message}
    </p>
  );
}

interface PaymentLocationState {
  orderId?: string;
  orderNumber?: string;
}

export default function PaymentPage() {
  const { language } = useLanguage();
  const formatPrice = useFormatPrice();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

    const { requireAuth } = useAuthGate();
  

  // Read order info from route state (passed from CartPage after place-order)
  const routeState = (location.state as PaymentLocationState) || {};
  const { orderId, orderNumber } = routeState;

  const [cardNumber, setCardNumber] = useState("");
  const [cardName, setCardName] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const [processing, setProcessing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);

  const isAr = language === "ar";

  // If no order context, redirect to cart
  if (!orderId && !completed) {
    requireAuth(() => navigate("/cart", { replace: true }));
    return null;
  }

  const formatCardNumber = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(.{4})/g, "$1 ").trim();
  };

  const formatExpiry = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 4);
    if (digits.length >= 3) return digits.slice(0, 2) + "/" + digits.slice(2);
    return digits;
  };

  // Validation
  const getErrors = (): FieldErrors => {
    const errors: FieldErrors = {};
    const rawCard = cardNumber.replace(/\s/g, "");

    if (rawCard.length === 0) {
      errors.cardNumber = isAr ? "رقم البطاقة مطلوب" : "Card number is required";
    } else if (rawCard.length < 13) {
      errors.cardNumber = isAr ? "رقم البطاقة قصير جداً" : "Card number is too short";
    } else if (!luhnCheck(rawCard)) {
      errors.cardNumber = isAr ? "رقم البطاقة غير صالح" : "Invalid card number";
    }

    if (!cardName.trim()) {
      errors.cardName = isAr ? "اسم حامل البطاقة مطلوب" : "Cardholder name is required";
    } else if (cardName.trim().length < 2) {
      errors.cardName = isAr ? "الاسم قصير جداً" : "Name is too short";
    }

    if (!expiry) {
      errors.expiry = isAr ? "تاريخ الانتهاء مطلوب" : "Expiry date is required";
    } else {
      const result = validateExpiry(expiry);
      if (!result.valid) {
        if (result.reason === "month") errors.expiry = isAr ? "الشهر غير صالح" : "Invalid month";
        else if (result.reason === "expired") errors.expiry = isAr ? "البطاقة منتهية الصلاحية" : "Card has expired";
        else errors.expiry = isAr ? "الصيغة: MM/YY" : "Use format MM/YY";
      }
    }

    if (!cvv) {
      errors.cvv = isAr ? "CVV مطلوب" : "CVV is required";
    } else if (cvv.length < 3) {
      errors.cvv = isAr ? "CVV غير صالح" : "Invalid CVV";
    }

    return errors;
  };

  const errors = getErrors();
  const showError = (field: string) => (submitted || touched[field]) ? errors[field as keyof FieldErrors] : undefined;
  const hasErrors = Object.keys(errors).length > 0;

  const handlePay = async () => {
    setSubmitted(true);
    if (hasErrors) return;

    setProcessing(true);
    await new Promise((r) => setTimeout(r, 2000));
    setProcessing(false);
    setCompleted(true);
    toast({
      title: isAr ? "تم الدفع بنجاح!" : "Payment successful!",
      description: isAr ? "جاري تحضير طلبك" : "Your order is being prepared",
    });
    setTimeout(() => {
      navigate(`/track/${orderId}`, { replace: true });
    }, 2000);
  };

  if (completed) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center space-y-4">
            <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-green-100 mx-auto">
              <Check className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground">
              {isAr ? "تم الدفع بنجاح! 🎉" : "Payment Successful! 🎉"}
            </h1>
            <p className="text-muted-foreground">
              {isAr ? "جاري تحويلك لتتبع الطلب..." : "Redirecting to order tracking..."}
            </p>
            {orderNumber && (
              <p className="text-sm text-muted-foreground">
                {isAr ? `رقم الطلب: ${orderNumber}` : `Order #${orderNumber}`}
              </p>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Detect card brand from first digits
  const rawDigits = cardNumber.replace(/\s/g, "");
  const cardBrand = rawDigits.startsWith("4") ? "Visa" : rawDigits.startsWith("5") ? "Mastercard" : rawDigits.startsWith("3") ? "Amex" : null;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-6 max-w-lg">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => requireAuth(() =>  navigate("/cart"))}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-heading text-2xl font-bold text-foreground">
            {isAr ? "الدفع" : "Payment"}
          </h1>
        </div>

        {orderNumber && (
          <div className="mb-4 text-sm text-muted-foreground text-center">
            {isAr ? `طلب رقم: ${orderNumber}` : `Order #${orderNumber}`}
          </div>
        )}

        <Card className="mb-6">
          <CardContent className="pt-6 space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <Label>{isAr ? "رقم البطاقة" : "Card Number"}</Label>
                {cardBrand && (
                  <span className="text-xs font-medium text-muted-foreground">{cardBrand}</span>
                )}
              </div>
              <div className="relative">
                <CreditCard className="absolute start-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="1234 5678 9012 3456"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                  onBlur={() => setTouched((p) => ({ ...p, cardNumber: true }))}
                  className={`ps-10 font-mono tracking-wider ${showError("cardNumber") ? "border-destructive focus-visible:ring-destructive" : ""}`}
                  maxLength={19}
                />
              </div>
              <InlineError message={showError("cardNumber")} />
            </div>

            <div>
              <Label>{isAr ? "اسم حامل البطاقة" : "Cardholder Name"}</Label>
              <Input
                placeholder={isAr ? "الاسم الكامل" : "Full Name"}
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
                onBlur={() => setTouched((p) => ({ ...p, cardName: true }))}
                className={showError("cardName") ? "border-destructive focus-visible:ring-destructive" : ""}
              />
              <InlineError message={showError("cardName")} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{isAr ? "تاريخ الانتهاء" : "Expiry"}</Label>
                <Input
                  placeholder="MM/YY"
                  value={expiry}
                  onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                  onBlur={() => setTouched((p) => ({ ...p, expiry: true }))}
                  maxLength={5}
                  className={`font-mono ${showError("expiry") ? "border-destructive focus-visible:ring-destructive" : ""}`}
                />
                <InlineError message={showError("expiry")} />
              </div>
              <div>
                <Label>CVV</Label>
                <Input
                  type="password"
                  placeholder="•••"
                  value={cvv}
                  onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  onBlur={() => setTouched((p) => ({ ...p, cvv: true }))}
                  maxLength={4}
                  className={`font-mono ${showError("cvv") ? "border-destructive focus-visible:ring-destructive" : ""}`}
                />
                <InlineError message={showError("cvv")} />
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2">
              <Lock className="h-3 w-3" />
              {isAr ? "معلومات الدفع مشفرة وآمنة" : "Your payment info is encrypted and secure"}
            </div>
          </CardContent>
        </Card>

        <Button
          size="lg"
          className="w-full gap-2 py-6 text-base font-bold rounded-xl"
          disabled={processing}
          onClick={handlePay}
        >
          {processing ? (
            <>{isAr ? "جاري المعالجة..." : "Processing..."}</>
          ) : (
            <>
              {isAr ? "ادفع الآن" : "Pay Now"}
              <ArrowRight className="h-5 w-5" />
            </>
          )}
        </Button>
      </main>

      <Footer />
    </div>
  );
}
